import HeaderMenuImage from '@/components/sections/builder/elements/header/header/header-menu-image';
import { HeaderElementDataType } from '@/components/sections/builder/elements/header/header/type';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { IMenuImage, IMenuItem } from '@/types/navigation';
import Link from 'next/link';

type MegaMenuProps = {
  menu: IMenuItem[];
  menuImages: IMenuImage[];
  themeId?: string;
  token?: string;
  preview?: boolean;
  createUrl: (
    url_path: string,
    opts?: {
      themeId?: string;
      token?: string;
      preview?: boolean;
    }
  ) => string;
  data: HeaderElementDataType;
};

export function MegaMenu({
  menu,
  menuImages,
  themeId,
  token,
  preview,
  createUrl,
  data,
}: MegaMenuProps) {
  return (
    <div
      className={cn(
        'mx-auto grid p-10',
        'lg:max-w-[1440px]',
        menuImages && menuImages.length > 0 ? 'grid-cols-3' : 'grid-cols-1'
      )}
    >
      <div
        className={cn(
          'col-span-2 grid w-full gap-6 delay-150 duration-500 animate-in fade-in slide-in-from-bottom-6',
          'mx-auto lg:max-w-[1280px]',
          menuImages && menuImages.length > 0
            ? `lg:grid-cols-${Math.min(menu.length, 4)} xl:grid-cols-${Math.min(menu.length, 5)}`
            : `grid-cols-` + Math.min(menu.length, 6)
        )}
      >
        {/* Main Categories */}
        {menu.map((subMenu) =>
          subMenu.children.length > 0 ? (
            <div className='group-ani' key={subMenu.id}>
              <Link
                href={createUrl(subMenu.url_path || '', {
                  themeId,
                  token,
                  preview,
                })}
                data-testid={`mega-menu-${subMenu.title}`}
              >
                <h3 className='mb-5 text-lg font-semibold'>{subMenu.title}</h3>
              </Link>
              <div className='flex flex-col gap-4'>
                {subMenu.children.map((childMenu) => (
                  <div key={childMenu.id}>
                    <NavigationMenuLink asChild>
                      <Link
                        href={createUrl(childMenu.url_path || '', {
                          themeId,
                          token,
                          preview,
                        })}
                      >
                        <span className='menu-item-with-hover-border text-hover-border text-base'>
                          {childMenu.title}
                        </span>
                      </Link>
                    </NavigationMenuLink>

                    {childMenu.children && childMenu.children.length > 0 && (
                      <div className='ml-4 mt-2 flex flex-col gap-2'>
                        {childMenu.children.map((subChild) => (
                          <NavigationMenuLink key={subChild.id} asChild>
                            <Link
                              href={createUrl(subChild.url_path || '', {
                                themeId,
                                token,
                                preview,
                              })}
                            >
                              <span className='menu-item-with-hover-border text-hover-border text-base'>
                                {subChild.title}
                              </span>
                            </Link>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <NavigationMenuLink key={subMenu.id} asChild>
              <Link
                href={createUrl(subMenu.url_path || '', {
                  themeId,
                  token,
                  preview,
                })}
              >
                <span className='menu-item-with-hover-border text-hover-border text-base'>
                  {subMenu.title}
                </span>
              </Link>
            </NavigationMenuLink>
          )
        )}
      </div>
      <div className='flex w-full justify-end gap-2'>
        {menuImages &&
          menuImages.length > 0 &&
          menuImages.map((image, idx) => (
            <div key={idx}>
              <Link
                href={createUrl(image.url_path || '', {
                  themeId,
                  token,
                  preview,
                })}
                passHref={image.url_path?.includes('http')}
                data-animation='fade-up'
              >
                <HeaderMenuImage title={image.title} src={image.image_url} parentData={data} />
              </Link>
            </div>
          ))}
      </div>
    </div>
  );
}
